import {Box, Typography} from '@mui/material';
import {useEffect, useMemo} from 'react';
import LayerAccordionItem from './LayerAccordionItem.tsx';
import {useGetCollectionLayers} from '@src/api/configurations';
import ErrorMessage from '@src/components/common/ErrorMessage.tsx';
import LoadingMessage from '@src/components/common/LoadingMessage.tsx';
import {SUPER_RES_LAYER_ID} from '@src/constants/analyze.ts';
import {useSuperResLayer} from '@src/hooks/useSuperResLayer.tsx';
import useVisualizationParams from '@src/hooks/useVisualizationParams';
import dimensions from '@src/styles/dimensions.ts';
import {Layer} from '@src/types/configurations';

export const LayerSelection = () => {
  const {configurationId, collectionId, layerId, setLayerId, isSuperRes} =
    useVisualizationParams();

  const {
    layers,
    isLoading,
    error: layersError,
  } = useGetCollectionLayers(configurationId, collectionId);

  const {layer: superResCapableLayer} = useSuperResLayer();

  const error = useMemo(() => {
    if (!configurationId) {
      return {message: 'You must select configuration'};
    }
    if (!collectionId) {
      return {message: 'You must select collection'};
    }
    return layersError;
  }, [layersError, configurationId, collectionId]);

  const handleLayerSelect = (
    selectedLayerId: string | undefined,
    e: React.MouseEvent,
  ) => {
    if (!selectedLayerId || selectedLayerId === SUPER_RES_LAYER_ID) {
      return;
    }
    e.stopPropagation();
    setLayerId(selectedLayerId);
  };

  const layersWithSuperRes = useMemo(() => {
    if (!layers || !isSuperRes || !superResCapableLayer) {
      return layers;
    }

    const superResCapableIndex = layers.findIndex(
      layer => layer.id === superResCapableLayer.id,
    );

    if (superResCapableIndex === -1) {
      return layers;
    }

    const artificialSuperResLayer: Layer = {
      ...superResCapableLayer,
      id: SUPER_RES_LAYER_ID,
      title: 'SuperRes - based on True Color',
      description: '',
      styles: [],
    };

    const newLayers = [...layers];
    newLayers.splice(superResCapableIndex, 0, artificialSuperResLayer);
    return newLayers;
  }, [layers, isSuperRes, superResCapableLayer]);

  const firstLayerId = layers?.[0]?.id;

  useEffect(() => {
    if (!layerId && firstLayerId) {
      setLayerId(firstLayerId);
    }
  }, [layerId, firstLayerId, setLayerId]);

  if (isLoading) {
    return (
      <LoadingMessage rootProps={{sx: {blockSize: 'auto', paddingBlock: 2}}} />
    );
  }

  if (error) {
    return (
      <ErrorMessage>
        <Typography variant="h6" color="inherit" gutterBottom>
          {error.message}
        </Typography>
      </ErrorMessage>
    );
  }

  return (
    <Box
      display="flex"
      flexDirection="column"
      sx={{
        m: `-${dimensions.leftSidebar.contentMargin}px`,
        height: `calc(100% + ${2 * dimensions.leftSidebar.contentMargin}px)`,
      }}
    >
      {layersWithSuperRes?.map(layer => {
        return (
          <LayerAccordionItem
            key={layer.id}
            layer={layer}
            isSelected={layer.id === layerId}
            onClick={handleLayerSelect}
          />
        );
      })}
    </Box>
  );
};

export default LayerSelection;
